import { useState } from 'react'

import { useAppDispatch, useAppSelector } from 'shared/config/store'
import { useAbortController } from 'shared/hooks/useAbortController'

import {
  updateBoardFieldThunk,
  deleteBoardFieldThunk,
  fetchBoardDataThunk
} from '../model/slice/boardsSlice'
import {
  getStatusFieldId,
  getSystemTagsId,
  getDescriptionFieldId
} from '../model/selectors/boardsSelectors'

export type BoardFieldName = 'status' | 'tags' | 'description'

interface UseBoardFieldsReturn {
  updateField: (fieldName: BoardFieldName, value: any) => Promise<void>
  deleteField: (fieldName: BoardFieldName) => Promise<void>
  isLoading: boolean
  error: string | null
}

/**
 * Custom hook for managing board fields (status, tags, description)
 * Provides unified interface for field operations with proper error handling
 */
export const useBoardFields = (boardId: string): UseBoardFieldsReturn => {
  const dispatch = useAppDispatch()
  const { createController, cleanup } = useAbortController()

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Get field IDs from selectors
  const statusFieldId = useAppSelector(getStatusFieldId)
  const tagsFieldId = useAppSelector(getSystemTagsId)
  const descriptionFieldId = useAppSelector(getDescriptionFieldId)

  const fieldIdMap: Record<BoardFieldName, string | null> = {
    status: statusFieldId,
    tags: tagsFieldId,
    description: descriptionFieldId
  }

  /**
   * Update field value
   * @param fieldName - Field to update
   * @param value - New value for the field
   */
  const updateField = async (fieldName: BoardFieldName, value: any): Promise<void> => {
    const fieldId = fieldIdMap[fieldName]
    if (!fieldId) {
      console.warn(`Field ID not found for field: ${fieldName}`)
      return
    }

    setIsLoading(true)
    setError(null)

    const signal = createController()

    try {
      await dispatch(updateBoardFieldThunk({
        nodeId: boardId,
        fieldID: fieldId,
        data: { value },
        signal,
        fieldName
      })).unwrap()
    } catch (err) {
      if ((err as Error)?.name !== 'AbortError') {
        const errorMessage = err instanceof Error ? err.message : `Failed to update ${fieldName}`
        setError(errorMessage)
        console.error(`Error updating board field ${fieldName}:`, err)
        throw err
      }
    } finally {
      setIsLoading(false)
      cleanup()
    }
  }

  /**
   * Delete field value
   * @param fieldName - Field to delete
   */
  const deleteField = async (fieldName: BoardFieldName): Promise<void> => {
    const fieldId = fieldIdMap[fieldName]
    if (!fieldId) {
      console.warn(`Field ID not found for field: ${fieldName}`)
      return
    }

    setIsLoading(true)
    setError(null)

    const signal = createController()

    try {
      await dispatch(deleteBoardFieldThunk({
        nodeId: boardId,
        fieldID: fieldId,
        signal
      })).unwrap()

      // Refresh board data after deletion to ensure consistency
      await dispatch(fetchBoardDataThunk({ nodeId: boardId })).unwrap()
    } catch (err) {
      if ((err as Error)?.name !== 'AbortError') {
        const errorMessage = err instanceof Error ? err.message : `Failed to delete ${fieldName}`
        setError(errorMessage)
        console.error(`Error deleting board field ${fieldName}:`, err)
        throw err
      }
    } finally {
      setIsLoading(false)
      cleanup()
    }
  }

  return {
    updateField,
    deleteField,
    isLoading,
    error
  }
}
